import React, { useState, useCallback, useEffect } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  Select,
  Tag,
  Descriptions,
  Radio,
  Drawer,
  Form,
  Row,
  Col,
  Image,
  Alert,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import {
  adminApi,
  CompanyApplication,
  CompanyApplicationDecision,
} from "@app/api/admin";
import dayjs from "dayjs";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import {
  initialPagination,
  initialPaginationProps,
  useCompanyList,
} from "./CompanyPage";
import { useDepartment } from "./DepartmentPage";
import { useNavigate } from "react-router-dom";
import { EmployeeStatusEnum } from "./employee/EmployeePage";

interface ICompanyApplication extends CompanyApplication {
  company_name: string;
}

const CompanyApplicationPage: React.FC = () => {
  // 获取部门列表
  const { departmentList } = useDepartment();

  // 根据部门ID获取部门名称
  const getDepartmentName = (departmentId?: number) => {
    if (!departmentId) return "-";
    const department = departmentList.find((dept) => dept.id === departmentId);
    return department?.name || "-";
  };

  const [tableData, setTableData] = useState<{
    data: CompanyApplication[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);

  // 详情抽屉
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [currentApplication, setCurrentApplication] =
    useState<ICompanyApplication | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);

  // 审批模态框
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalRemark, setApprovalRemark] = useState("");
  const [approvalDecision, setApprovalDecision] = useState<
    "approved" | "rejected"
  >("approved");
  const navigate = useNavigate();

  const [searchForm] = Form.useForm();

  const [searchParams, setSearchParams] = useState<{
    keyword?: string;
    status?: "pending" | "approved" | "rejected";
  }>({});

  // 添加分页状态
  const [pagination, setPagination] = useState(initialPagination);

  // 获取申请列表数据 - 简化函数，不接受参数
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const { total, ...rest } = pagination;
      const { data } = await adminApi.companyApplications.list({
        ...rest,
        ...searchParams,
      });

      setTableData({
        data: data.applications || [],
        pagination: data.pagination,
      });
    } catch (error: any) {
      console.error(error);
      // message.error(error.message || "获取申请列表失败");
    } finally {
      setLoading(false);
    }
  };

  // 监听搜索条件和分页变化
  useUpdateEffect(() => {
    fetchApplications();
  }, [searchParams, pagination]);

  // 搜索处理
  const handleSearch = (values: any) => {
    setSearchParams(values);
    setPagination({ ...initialPagination, page: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  // 分页变化处理
  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  // 查看详情
  const handleViewDetail = async (record: CompanyApplication) => {
    try {
      setDetailLoading(true);
      const { data } = await adminApi.companyApplications.detail(record.id);
      const { data: companies } = await adminApi.companies.detail(
        data.company_id
      );
      setCurrentApplication({ ...data, company_name: companies.name });
      setDetailDrawerVisible(true);
    } catch (error: any) {
      message.error(error.message || "获取申请详情失败");
    } finally {
      setDetailLoading(false);
    }
  };

  // 审批申请
  const handleApproval = async (record: CompanyApplication) => {
    try {
      const { data: companies } = await adminApi.companies.detail(
        record.company_id
      );
      setCurrentApplication({ ...record, company_name: companies.name });
      setApprovalDecision("approved");
      setApprovalRemark("");
      setApprovalModalVisible(true);
    } catch (error: any) {
      message.error(error.message || "获取公司信息失败");
    }
  };

  // 提交审批
  const handleApprovalSubmit = async () => {
    if (!currentApplication) return;

    // 如果选择拒绝但没有填写理由，提示用户
    if (approvalDecision === "rejected" && !approvalRemark.trim()) {
      message.error("拒绝申请时必须填写拒绝理由");
      return;
    }

    try {
      setApprovalLoading(true);
      let queryParams: CompanyApplicationDecision = {
        decision: approvalDecision,
      };
      if (approvalDecision === "rejected") {
        queryParams = {
          ...queryParams,
          reject_reason: approvalRemark,
        };
      }
      if (approvalDecision === "approved") {
        queryParams = {
          ...queryParams,
          remark: approvalRemark,
        };
      }
      const { data } = await adminApi.companyApplications.decision(
        currentApplication.id,
        queryParams
      );

      if (approvalDecision === "approved") {
        Modal.confirm({
          title: `当前员工${currentApplication.name}入驻申请已通过，是否立即添加为员工？`,
          okText: "确认",
          okType: "primary",
          okButtonProps: {
            loading: approvalLoading,
          },
          cancelText: "取消",
          onOk: async () => {
            try {
              const submitData = {
                user_id: currentApplication.user_id,
                name: currentApplication.name,
                gender: currentApplication.gender,
                phone_number: currentApplication.phone,
                id_card_number: currentApplication.id_card_number,
                department_id: currentApplication.department_id,
                company_id: currentApplication.company_id,
                // position: currentApplication.position, // Employee create might not accept position directly if not in interface, but let's include if api supports
                education: currentApplication.education,
                political_status: currentApplication.political_status,
                birthday_type: currentApplication.birthday_type,
                birthday: currentApplication.birthday
                  ? dayjs(currentApplication.birthday).format("YYYY-MM-DD")
                  : undefined,
                hire_date: dayjs().format("YYYY-MM-DD"),
                status: EmployeeStatusEnum.OnJob,
                contract_type: "",
                salary_payment_method: "",
                participate_seniority_calculation: false,
                id_card_front_url: currentApplication.id_card_front_url || "",
                id_card_back_url: currentApplication.id_card_back_url || "",
                driver_license_front_url:
                  currentApplication.driver_license_front_url || "",
                driver_license_back_url:
                  currentApplication.driver_license_back_url || "",
              };
              await adminApi.employees.create(submitData);
              message.success("员工创建成功");
              // navigate(`/employee`);
            } catch (error: any) {
              console.error("创建员工失败", error);
              message.error(error.message || "创建员工失败");
            }
          },
        });
      } else {
        message.success(`申请已拒绝`);
      }
      setApprovalModalVisible(false);
      fetchApplications();
    } catch (error: any) {
      message.error(error.message || "审批失败");
    } finally {
      setApprovalLoading(false);
    }
  };

  

  // 状态标签渲染
  const renderStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: "orange", text: "待审批" },
      approved: { color: "green", text: "已通过" },
      rejected: { color: "red", text: "已拒绝" },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "default",
      text: status,
    };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const [columns] = useState([
    {
      title: "用户姓名",
      dataIndex: "name",
      width: 120,
    },
    {
      title: "应聘公司",
      dataIndex: "company_name",
      width: 180,
    },
    {
      title: "应聘岗位",
      dataIndex: "position",
      width: 120,
    },
    {
      title: "手机号",
      dataIndex: "phone",
      width: 120,
    },
    {
      title: "审核状态",
      dataIndex: "status",
      width: 120,
      render: (status: string) => renderStatusTag(status),
    },
    {
      title: "申请时间",
      dataIndex: "created_at",
      width: 120,
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
    // reviewed_at
    {
      title: "审核时间",
      dataIndex: "reviewed_at",
      width: 120,
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
    // {
    //   title: "备注",
    //   dataIndex: "remark",
    //   width: "15%",
    //   ellipsis: true,
    // },
    {
      title: "操作",
      dataIndex: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: CompanyApplication) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
              loading={detailLoading}
              size="small"
            >
              详情
            </BaseButton>
            {record.status === "pending" && (
              <BaseButton
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleApproval(record)}
                size="small"
              >
                审核
              </BaseButton>
            )}
          </BaseSpace>
        );
      },
    },
  ]);
  const { companyList } = useCompanyList();

  return (
    <>
      <PageTitle>入驻申请管理</PageTitle>
      <Alert
        type="info"
        message="页面功能说明"
        description={
          <>
            此页面展示用户入驻公司申请，审批同意后可在{" "}
            <a onClick={() => navigate(`/employee`)}>员工管理</a>中添加为员工
          </>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />
      {/* 搜索表单 */}
      <Form
        form={searchForm}
        onFinish={handleSearch}
        layout="inline"
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Form.Item name="search" style={{ marginBottom: 0 }}>
              <Input placeholder="用户姓名/手机号" allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6} lg={6}>
            <Form.Item name="status" style={{ marginBottom: 0 }}>
              <Select
                placeholder="审核状态"
                allowClear
                style={{ width: "100%" }}
              >
                <Select.Option value="pending">待审批</Select.Option>
                <Select.Option value="approved">已通过</Select.Option>
                <Select.Option value="rejected">已拒绝</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Form.Item name="company_id">
              <Select placeholder="请选择公司">
                {companyList.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={4} lg={6}>
            <Form.Item style={{ marginBottom: 0 }}>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset} icon={<RedoOutlined />}>
                  重置
                </BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        pagination={{
          ...tableData.pagination,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1600 }}
      />

      {/* 详情抽屉 */}
      <Drawer
        title="申请详情"
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false);
          setCurrentApplication(null);
        }}
        width={600}
        placement="right"
        footer={
          currentApplication?.status === "pending" && (
            <BaseSpace>
              <BaseButton
                type="primary"
                onClick={() => {
                  setDetailDrawerVisible(false);
                  handleApproval(currentApplication!);
                }}
              >
                审核
              </BaseButton>
            </BaseSpace>
          )
        }
      >
        {currentApplication && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="姓名">
              {currentApplication.name}
            </Descriptions.Item>
            <Descriptions.Item label="性别">
              {currentApplication.gender || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="手机号">
              {currentApplication.phone}
            </Descriptions.Item>
            <Descriptions.Item label="身份证号">
              {currentApplication.id_card_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="应聘公司名称">
              {currentApplication.company_name}
            </Descriptions.Item>
            <Descriptions.Item label="应聘部门">
              {currentApplication.department_id
                ? getDepartmentName(currentApplication.department_id)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="应聘岗位">
              {currentApplication.position}
            </Descriptions.Item>
            <Descriptions.Item label="学历">
              {currentApplication.education || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="政治面貌">
              {currentApplication.political_status || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="生日类型">
              {currentApplication.birthday_type || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="生日">
              {currentApplication.birthday
                ? new Date(currentApplication.birthday).toLocaleDateString()
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="备注">
              {currentApplication.remark || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="身份证正面">
              {currentApplication.id_card_front_url ? (
                <Image
                  width={100}
                  height={60}
                  src={currentApplication.id_card_front_url}
                  placeholder="加载中..."
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="身份证反面">
              {currentApplication.id_card_back_url ? (
                <Image
                  width={100}
                  height={60}
                  src={currentApplication.id_card_back_url}
                  placeholder="加载中..."
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="驾驶证正面">
              {currentApplication.driver_license_front_url ? (
                <Image
                  width={100}
                  height={60}
                  src={currentApplication.driver_license_front_url}
                  placeholder="加载中..."
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="驾驶证反面">
              {currentApplication.driver_license_back_url ? (
                <Image
                  width={100}
                  height={60}
                  src={currentApplication.driver_license_back_url}
                  placeholder="加载中..."
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {renderStatusTag(currentApplication.status)}
            </Descriptions.Item>
            <Descriptions.Item label="审核人">
              {currentApplication.reviewed_by || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="审核时间">
              {currentApplication.reviewed_at
                ? new Date(currentApplication.reviewed_at).toLocaleString()
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                currentApplication.status === "rejected"
                  ? "拒绝理由"
                  : "审批备注"
              }
            >
              {currentApplication.reject_reason || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {new Date(currentApplication.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {new Date(currentApplication.updated_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* 审批模态框 */}
      <Modal
        title="审核申请"
        visible={approvalModalVisible}
        onOk={handleApprovalSubmit}
        onCancel={() => {
          setApprovalModalVisible(false);
          setCurrentApplication(null);
        }}
        confirmLoading={approvalLoading}
        width={600}
        okText={approvalDecision === "approved" ? "通过" : "拒绝"}
        okButtonProps={{
          danger: approvalDecision === "rejected",
          type: approvalDecision === "approved" ? "primary" : "default",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            审核决定：
          </label>
          <Radio.Group
            value={approvalDecision}
            onChange={(e) => setApprovalDecision(e.target.value)}
          >
            <Radio value="approved">
              <CheckOutlined style={{ color: "#52c41a", marginRight: 4 }} />
              通过申请
            </Radio>
            <Radio value="rejected">
              <CloseOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />
              拒绝申请
            </Radio>
          </Radio.Group>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 8 }}>
            {approvalDecision === "rejected" ? "拒绝理由：" : "审批备注："}
            {approvalDecision === "rejected" && (
              <span style={{ color: "#ff4d4f" }}>*</span>
            )}
          </label>
          <Input.TextArea
            rows={4}
            value={approvalRemark}
            onChange={(e) => setApprovalRemark(e.target.value)}
            placeholder={
              approvalDecision === "rejected"
                ? "请填写拒绝理由（必填）"
                : "请输入审批备注（可选）"
            }
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </>
  );
};

export default CompanyApplicationPage;

export const useApplicationUser = () => {
  const [userList, setUserList] = useState<CompanyApplication[]>([]);

  const fetchUsers = async () => {
    try {
      const { data } = await adminApi.companyApplications.list({
        page: 1,
        page_size: 1000,
        status: "approved",
      });
      setUserList(data.applications || []);
    } catch (error: any) {
      console.error(error.message || "获取用户列表失败");
    }
  };

  useUpdateEffect(() => {
    fetchUsers();
  }, []);

  return {
    userList,
    fetchUsers,
  };
};
